import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { MdClose, MdVisibility, MdKeyboardArrowDown } from 'react-icons/md';
import ImageCropper from '../components/ImageCropper';
import WebPreviewModal from '../components/WebPreviewModal';
import DragDropImageUpload from '../components/DragDropImageUpload';
import { useAuth } from '../context/AuthContext';

function toLocalDatetimeString(date) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function SearchableSelect({ label, value, options, onChange, placeholder = "Search...", searchKey = "slug" }) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = options.filter(opt =>
    (opt.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (opt[searchKey] || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="form-group">
      <label>{label}</label>
      <div className="searchable-select">
        <div className="select-box" onClick={() => setIsOpen(!isOpen)}>
          {(typeof value === 'string' && (value.trim() === 'అల్ ఇండియా' || value.trim() === 'అల్ ఇండియా ') ? 'All India' : value) || `Select ${label}`}
        </div>
        {isOpen && (
          <div className="select-dropdown">
            <input
              autoFocus
              placeholder={placeholder}
              value={search}
              onChange={e => setSearch(e.target.value)}
              onClick={e => e.stopPropagation()}
            />
            <div className="options-list">
              {filtered.map(opt => (
                <div
                  key={opt.id || opt.name}
                  className="option-item"
                  onClick={() => {
                    onChange(opt.name);
                    setIsOpen(false);
                    setSearch('');
                  }}
                >
                  <span>{(opt.name?.trim() === 'అల్ ఇండియా' || opt.name?.trim() === 'అల్ ఇండియా ') ? 'All India' : opt.name}</span>
                  {opt[searchKey] && <span className="text-muted text-xs">({opt[searchKey]})</span>}
                </div>
              ))}
              {filtered.length === 0 && <div className="option-item text-muted">{options.length === 0 ? `No ${label.toLowerCase()}s available` : "No results found"}</div>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function OthersDropdown({ options, value, onChange, quickCategoriesList }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isOtherSelected = value && !quickCategoriesList.includes(value.trim());
  const displayLabel = isOtherSelected ? value : 'Others';

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        style={{
          display: 'flex', alignItems: 'center', gap: '8px',
          padding: '6px 12px', borderRadius: '6px', border: '1px solid #ddd',
          background: isOtherSelected ? '#FFF1F2' : '#fff',
          color: isOtherSelected ? '#EF233C' : '#333',
          fontSize: '14px', cursor: 'pointer', outline: 'none'
        }}
      >
        {displayLabel} <MdKeyboardArrowDown style={{ fontSize: '18px' }} />
      </button>
      {isOpen && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, marginTop: '4px',
          background: '#fff', border: '1px solid #eee', borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 10,
          maxHeight: '250px', overflowY: 'auto', minWidth: '160px',
          padding: '4px'
        }}>
          {options.map(opt => (
            <div
              key={opt.name}
              onClick={() => { onChange(opt.name); setIsOpen(false); }}
              style={{
                padding: '8px 12px', fontSize: '14px', cursor: 'pointer',
                borderRadius: '4px',
                background: value === opt.name ? '#FFF1F2' : 'transparent',
                color: value === opt.name ? '#EF233C' : '#333'
              }}
              onMouseOver={(e) => { if (value !== opt.name) e.target.style.background = '#f5f5f5'; }}
              onMouseOut={(e) => { if (value !== opt.name) e.target.style.background = 'transparent'; }}
            >
              {opt.name}
            </div>
          ))}
          {options.length === 0 && <div style={{ padding: '8px', fontSize: '14px', color: '#888' }}>No other categories</div>}
        </div>
      )}
    </div>
  );
}

// The full category list (quick buttons + Others dropdown) and the State/City
// pickers are hidden per product request, leaving only a single "News"
// category plus Breaking/Featured. All underlying state, fetching, and
// submission logic is kept intact so these can be re-enabled by flipping
// these flags back to true.
const SHOW_FULL_CATEGORY_PICKER = false;
const SHOW_STATE_CITY_PICKER = false;

export default function PublishArticle() {
  const navigate = useNavigate();
  const { admin } = useAuth();
  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [allCities, setAllCities] = useState([]);
  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState('');
  const [form, setForm] = useState({
    title: '', summary: '', source_url: '', source: '', media_type: 'image',
    image_url: '', video_url: '', thumbnail_url: '', images: [],
    category: 'News', city: '', is_breaking: false, is_featured: false,
    published_at: toLocalDatetimeString(new Date()),
    slug: '',
    author_name: '',
    status: 'published',
    manual_thumbnail: false,
    title_en: '', summary_en: '',
    title_hi: '', summary_hi: '',
  });

  const stateMapping = {
    'ఆంధ్రప్రదేశ్': 'Andhra Pradesh',
    'తెలంగాణ': 'Telangana',
    'అల్ ఇండియా': 'All India',
    'అల్ ఇండియా ': 'All India',
    'ఆంధ్ర ప్రదేశ్': 'Andhra Pradesh',
    'Telangana': 'Telangana',
    'Andhra Pradesh': 'Andhra Pradesh'
  };

  useEffect(() => {
    api.get('/categories').then(res => {
      setCategories(res.data.data);
      if (res.data.data.length > 0) {
        const defaultCategory = SHOW_FULL_CATEGORY_PICKER
          ? res.data.data[0]
          : (res.data.data.find(c => c.name.trim().toLowerCase() === 'news') || res.data.data[0]);
        setForm(f => ({ ...f, category: defaultCategory.name }));
      }
    }).catch(() => { });

    api.get('/cities').then(res => {
      const all = res.data.data || [];
      setAllCities(all);

      const getCityState = (c) => (c.State ? c.State.name : (c.state || '')).trim();
      const rawStates = all.map(getCityState).filter(Boolean);
      const uniqueStatesList = [...new Set(rawStates)];

      const stateOptions = uniqueStatesList.map(s => ({
        name: s,
        slug: stateMapping[s] || ''
      }));

      setStates(stateOptions);

      const defaultState = uniqueStatesList.includes('Telangana') ? 'Telangana' : (uniqueStatesList[0] || 'All India');
      setSelectedState(defaultState);
    }).catch(() => { });
  }, []);

  useEffect(() => {
    if (selectedState && allCities.length > 0) {
      const stateName = selectedState.trim();
      const getCityState = (c) => (c.State ? c.State.name : (c.state || '')).trim();

      const filtered = allCities.filter(c => {
        const cState = getCityState(c);
        if (!cState) return false;
        if (stateName === 'Andhra Pradesh' || stateName === 'ఆంధ్రప్రదేశ్') {
          return cState === 'Andhra Pradesh' || cState === 'ఆంధ్రప్రదేశ్' || cState === 'ఆంధ్ర ప్రదేశ్';
        }
        if (stateName === 'Telangana' || stateName === 'తెలంగాణ') {
          return cState === 'Telangana' || cState === 'తెలంగాణ';
        }
        if (stateName === 'All India' || stateName === 'అల్ ఇండియా') {
          return cState === 'All India' || cState === 'అల్ ఇండియా' || cState === 'అల్ ఇండియా ';
        }
        return cState.toLowerCase() === stateName.toLowerCase();
      });

      setCities(filtered);

      let defaultCity = filtered.length > 0 ? filtered[0].name : '';

      if (stateName === 'ఆంధ్రప్రదేశ్' || stateName === 'Andhra Pradesh') {
        const amaravati = filtered.find(c => c.name.toLowerCase() === 'amaravati' || c.name === 'అమరావతి');
        defaultCity = amaravati ? amaravati.name : (defaultCity || 'Amaravati');
      } else if (stateName === 'తెలంగాణ' || stateName === 'Telangana') {
        const hyderabad = filtered.find(c => c.name.toLowerCase() === 'hyderabad' || c.name === 'హైదరాబాద్');
        defaultCity = hyderabad ? hyderabad.name : (defaultCity || 'Hyderabad');
      } else if (stateName === 'అల్ ఇండియా' || stateName === 'All India') {
        const allIndiaCity = filtered.find(c => c.name.trim() === 'All India' || c.name.trim() === 'అల్ ఇండియా');
        defaultCity = allIndiaCity ? allIndiaCity.name : 'All India';
      }

      setForm(f => ({ ...f, city: defaultCity || (filtered.length > 0 ? filtered[0].name : '') }));
    }
  }, [selectedState, allCities]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [showWebPreview, setShowWebPreview] = useState(false);
  const [errors, setErrors] = useState({});
  const [cropper, setCropper] = useState({ active: false, image: null, type: null });
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);
  const [viewMatchedArticle, setViewMatchedArticle] = useState(null);

  const handleGalleryFilesUpload = async (files) => {
    if (!files || files.length === 0) return;
    setGalleryUploading(true);
    const newUrls = [];
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await api.post('/admin/upload/image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        if (res.data?.data?.url) {
          newUrls.push(res.data.data.url);
        }
      } catch (err) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    if (newUrls.length > 0) {
      setForm(prev => ({
        ...prev,
        images: [...(prev.images || []), ...newUrls]
      }));
      toast.success(`${newUrls.length} gallery image(s) uploaded`);
    }
    setGalleryUploading(false);
  };

  const moveGalleryImage = (index, direction) => {
    setForm(prev => {
      const arr = [...(prev.images || [])];
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= arr.length) return prev;
      const temp = arr[index];
      arr[index] = arr[targetIndex];
      arr[targetIndex] = temp;
      return { ...prev, images: arr };
    });
  };

  const removeGalleryImage = (index) => {
    setForm(prev => ({
      ...prev,
      images: (prev.images || []).filter((_, i) => i !== index)
    }));
  };

  const handleGalleryPasteImage = (e) => {
    const items = e.clipboardData?.items || [];
    const filesToUpload = [];
    for (const it of items) {
      if (it.type && it.type.startsWith('image/')) {
        const file = it.getAsFile();
        if (file) {
          filesToUpload.push(file);
        }
      }
    }
    if (filesToUpload.length > 0) {
      e.preventDefault();
      e.stopPropagation();
      handleGalleryFilesUpload(filesToUpload);
    }
  };

  const wordCount = form.summary.trim().split(/\s+/).filter(Boolean).length;

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const validate = () => {
    const errs = {};
    if (form.title.length > 75) errs.title = 'Title cannot exceed 75 characters.';
    if (form.summary.length > 400) errs.summary = 'Summary cannot exceed 400 characters.';
    
    if (form.media_type === 'image') {
      if (!form.image_url || !form.image_url.trim()) {
        errs.image_url = 'Please upload the Article Image (Web & App).';
      }
    } else if (form.media_type === 'video') {
      if (!form.video_url || !form.video_url.trim()) {
        errs.video_url = 'Please upload the Article Video.';
      }
      if (!form.thumbnail_url || !form.thumbnail_url.trim()) {
        errs.thumbnail_url = 'Please upload the Article Video Thumbnail.';
      }
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleFileSelect = (fileOrType, type) => {
    const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

    if (typeof fileOrType === 'string') {
      const currentType = fileOrType;
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = currentType === 'video' ? 'video/mp4' : 'image/jpeg,image/jpg,image/png,image/webp';

      // Store reference to prevent garbage collection
      window._tempFileInput = input;

      input.onchange = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        const file = files[0];
        if (currentType === 'video') {
          if (file.type !== 'video/mp4') {
            return toast.error('Only MP4 videos are supported.');
          }
          if (file.size > 200 * 1024 * 1024) {
            return toast.error('Video file size exceeds 200 MB limit.');
          }
          uploadToServer(file, currentType);
        } else {
          if (file.type && !validImageTypes.includes(file.type.toLowerCase())) {
            return toast.error('Invalid file format. Supported formats: JPG, JPEG, PNG, WEBP.');
          }
          if (file.size > 5 * 1024 * 1024) {
            return toast.error('File size exceeds 5 MB limit. Please select a smaller file.');
          }
          const reader = new FileReader();
          reader.onload = () => {
            setCropper({ active: true, image: reader.result, type: currentType });
          };
          reader.readAsDataURL(file);
        }
      };

      input.click();
      return;
    }

    // Called from DragDropImageUpload with (file, type)
    const file = fileOrType;
    if (type === 'video') {
      if (file.type !== 'video/mp4') {
        return toast.error('Only MP4 videos are supported.');
      }
      if (file.size > 200 * 1024 * 1024) {
        return toast.error('Video file size exceeds 200 MB limit.');
      }
      uploadToServer(file, type);
    } else {
      if (file && file instanceof Blob) {
        if (file.type && !validImageTypes.includes(file.type.toLowerCase())) {
          return toast.error('Invalid file format. Supported formats: JPG, JPEG, PNG, WEBP.');
        }
        if (file.size > 5 * 1024 * 1024) {
          return toast.error('File size exceeds 5 MB limit. Please select a smaller file.');
        }
        const reader = new FileReader();
        reader.onload = () => {
          setCropper({ active: true, image: reader.result, type });
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const handleCropComplete = async (blob) => {
    const type = cropper.type;
    setCropper({ active: false, image: null, type: null });

    const file = new File([blob], `cropped-${Date.now()}.jpg`, { type: 'image/jpeg' });
    uploadToServer(file, type);
  };

  const uploadToServer = async (file, type) => {
    const formData = new FormData();
    formData.append('file', file);
    const endpoint = (type === 'image' || type === 'extra_image') ? '/admin/upload/image'
      : type === 'thumbnail' ? '/admin/upload/thumbnail'
        : '/admin/upload/video';

    setUploading(true);
    try {
      const res = await api.post(endpoint, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = res.data.data.url;
      const thumbnailUrl = res.data.data.thumbnail_url;

      if (type === 'image') {
        set('image_url', url);
        setErrors(prev => ({ ...prev, image_url: null }));
      }
      else if (type === 'extra_image') setForm(prev => ({ ...prev, images: [...(prev.images || []), url] }));
      else if (type === 'thumbnail') {
        set('thumbnail_url', url);
        set('manual_thumbnail', true);
        setErrors(prev => ({ ...prev, thumbnail_url: null }));
      }
      else if (type === 'video') {
        setForm(f => {
          const nextForm = { ...f, video_url: url };
          if (thumbnailUrl && !f.manual_thumbnail) {
            nextForm.thumbnail_url = thumbnailUrl;
          }
          return nextForm;
        });
        setErrors(prev => ({ ...prev, video_url: null }));
      }
      toast.success(`${type === 'extra_image' ? 'Image' : type} uploaded and resized!`);
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    const handleGlobalPaste = (e) => {
      if (form.media_type !== 'image') return;
      if (cropper.active || showWebPreview) return; // Don't intercept if other modals are open

      const items = e.clipboardData?.items || [];
      for (const it of items) {
        if (it.type && it.type.startsWith('image/')) {
          const file = it.getAsFile();
          if (file) {
            e.preventDefault();
            const reader = new FileReader();
            reader.onload = () => {
              setCropper({ active: true, image: reader.result, type: 'image' });
            };
            reader.readAsDataURL(file);
            return;
          }
        }
      }
    };

    window.addEventListener('paste', handleGlobalPaste);
    return () => window.removeEventListener('paste', handleGlobalPaste);
  }, [form.media_type, cropper.active, showWebPreview]);

  const handlePasteImage = (e) => {
    // Kept for the div's onPaste to prevent any default behavior, though global handler catches it.
    e.preventDefault();
  };

  const handleAiGenerate = async (type, extraData = null) => {
    if (type === 'slug' && !form.title) {
      return toast.error('Please enter a title first to generate a slug');
    }
    if (type === 'transliterate' && !extraData) {
      return toast.error('Please enter some text to convert');
    }

    setLoading(true);
    try {
      const payload = { type };
      if (type === 'slug') payload.title = form.title;
      if (type === 'transliterate') payload.text = extraData;

      const res = await api.post('/admin/ai/generate', payload);
      if (res.data.success) {
        if (type === 'slug') set('slug', res.data.data);
        if (type === 'author') set('author_name', res.data.data);
        if (type === 'transliterate') {
          return res.data.data; // Return for specific field updates
        }
        toast.success(`${type === 'slug' ? 'Slug' : 'Author name'} generated`);
      }
    } catch (err) {
      toast.error('AI operation failed');
    } finally {
      setLoading(false);
    }
  };

  const doPublish = async () => {
    setLoading(true);
    try {
      const payload = { ...form, category: form.category || 'News' };
      // By sending the raw string without 'Z', the backend will parse it
      // using its own local timezone (Asia/Kolkata), thus correctly
      // calculating the UTC time for the database.
      await api.post('/admin/articles', payload);
      toast.success(admin?.role === 'reporter' ? 'Draft saved!' : 'Article published!');
      navigate('/articles');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to publish');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.summary) return toast.error('Title and summary required');
    if (form.title.length > 75) return toast.error(`Title exceeds limit by ${form.title.length - 75} characters`);
    if (form.summary.length > 400) return toast.error(`Summary exceeds limit by ${form.summary.length - 400} characters`);
    if (!validate()) return toast.error('Please fix validation errors');

    // Duplicate-check feature disabled for now — commented out, not removed.
    // setCheckingDuplicate(true);
    // try {
    //   const res = await api.post('/admin/articles/check-duplicate', {
    //     title: form.title,
    //     summary: form.summary,
    //     category: form.category,
    //   });
    //   if (res.data.success && res.data.has_duplicates) {
    //     setDuplicateWarning({ duplicates: res.data.duplicates });
    //     return;
    //   }
    // } catch (err) {
    //   // Don't block publishing if the duplicate check itself fails.
    // } finally {
    //   setCheckingDuplicate(false);
    // }

    await doPublish();
  };

  const handleTransliterate = async (field) => {
    const text = form[field];
    if (!text) return toast.error(`Please type something in ${field} first`);
    const result = await handleAiGenerate('transliterate', text);
    if (result) {
      const limit = field === 'title' ? 75 : field === 'summary' ? 400 : result.length;
      set(field, result.slice(0, limit));
    }
  };

  const handleGenerateTranslations = async () => {
    if (!form.title || !form.summary) {
      return toast.error('Please enter Telugu title and summary first');
    }

    setLoading(true);
    let successCount = 0;

    try {
      // English Translation
      const enRes = await api.post('/admin/ai/generate', { type: 'translate', title: form.title, summary: form.summary, targetLanguage: 'English' });

      if (enRes.data.success && enRes.data.data.headline && enRes.data.data.body) {
        set('title_en', enRes.data.data.headline.slice(0, 150)); // allow a bit more for English
        set('summary_en', enRes.data.data.body.slice(0, 600));
        successCount++;
      } else {
        toast.error('English translation failed');
      }
    } catch (err) {
      toast.error('English translation error');
    }

    try {
      // Hindi Translation
      const hiRes = await api.post('/admin/ai/generate', { type: 'translate', title: form.title, summary: form.summary, targetLanguage: 'Hindi' });

      if (hiRes.data.success && hiRes.data.data.headline && hiRes.data.data.body) {
        set('title_hi', hiRes.data.data.headline.slice(0, 150));
        set('summary_hi', hiRes.data.data.body.slice(0, 600));
        successCount++;
      } else {
        toast.error('Hindi translation failed');
      }
    } catch (err) {
      toast.error('Hindi translation error');
    }

    setLoading(false);
    if (successCount > 0) toast.success('Translations generated successfully!');
  };

  const handleCopySingleTranslation = async (lang, title, summary) => {
    try {
      const reviewPrompt = "Please review this news as a journalist and an experienced news writer. Improve the content wherever necessary by correcting grammar, punctuation, sentence structure, clarity, factual consistency, and readability while following professional news editorial style guidelines. Preserve the original meaning and tone. Return only the revised headline and article.\n\n";
      const copyText = `${reviewPrompt}${lang} Headline:\n${title || ''}\n\n${lang} Article:\n${summary || ''}`;

      await navigator.clipboard.writeText(copyText);
      toast.success(`${lang} translation copied successfully!`);
    } catch (err) {
      toast.error(`Failed to copy ${lang.toLowerCase()} translation.`);
    }
  };

  return (
    <div className="page" style={{ paddingLeft: '16px', paddingRight: '16px' }}>
      <div className="publish-layout" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div>
            <p style={{ margin: 0, color: '#6B7280', fontSize: '13px', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Create New Article</p>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <select
              value={form.status}
              onChange={e => set('status', e.target.value)}
              disabled={admin?.role === 'reporter'}
              style={{ fontSize: '13px', padding: '6px 12px', border: '1px solid #E5E7EB', borderRadius: '6px', backgroundColor: '#FFFFFF', color: '#111827', cursor: 'pointer' }}
            >
              <option value="draft">📋 Draft</option>
              <option value="published">✓ Published</option>
            </select>
            <button
              type="button"
              className="btn btn-outline-red"
              onClick={() => setShowWebPreview(true)}
              style={{ fontSize: '14px', padding: '8px 16px' }}
            >
              👁️ Preview
            </button>
            <button
              type="submit"
              form="publish-form"
              className="btn btn-primary"
              disabled={loading || checkingDuplicate || form.title.length > 75 || form.summary.length > 400}
              style={{ fontSize: '14px', padding: '8px 16px' }}
            >
              {checkingDuplicate ? '🔍 Checking duplicates...' : loading ? '⏳ Publishing...' : '✓ Publish'}
            </button>
          </div>
        </div>

        <form id="publish-form" className="publish-form" onSubmit={handleSubmit} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', alignItems: 'flex-start', maxWidth: '100%' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)' }}>
            <div className="form-group">
              <div className="flex-row justify-between items-center mb-1">
                <label className="mb-0">Title</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{
                    fontSize: '13px',
                    fontWeight: '400',
                    color: form.title.length > 75 ? '#DC2626' : '#6B7280',
                    transition: 'color 0.2s'
                  }}>
                    {form.title.length} / 75
                  </span>
                  {form.title.length > 75 && (
                    <span style={{
                      fontSize: '12px',
                      background: '#FEE2E2',
                      color: '#DC2626',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontWeight: '400',
                      animation: 'pulse 1s infinite'
                    }}>
                      +{form.title.length - 75} extra
                    </span>
                  )}
                </div>
              </div>
              <input
                type="text"
                value={form.title}
                onChange={(e) => set('title', e.target.value)}
                placeholder="Type in English (e.g. namaskaram)..."
                style={{
                  borderColor: form.title.length > 75 ? '#DC2626' : undefined,
                  backgroundColor: form.title.length > 75 ? '#FEF2F2' : undefined
                }}
                required
              />
              {form.title.length > 75 && (
                <span style={{ fontSize: '12px', color: '#DC2626', marginTop: '6px', display: 'block' }}>
                  ⚠️ Title exceeds limit by {form.title.length - 75} characters. Please trim it down to 75 characters max.
                </span>
              )}
              {errors.title && <span className="field-error">{errors.title}</span>}
            </div>

            {/* <div className="form-group">
            <div className="flex-row justify-between items-center mb-1">
              <label className="mb-0">URL Slug (English only)</label>
              <button type="button" className="btn btn-xs btn-ghost ai-btn" onClick={() => handleAiGenerate('slug')} disabled={loading}>
                ✨ AI Generate
              </button>
            </div>
            <input
              type="text"
              value={form.slug}
              onChange={(e) => set('slug', e.target.value)}
              placeholder="e.g. telangana-politics (auto-generated if empty)"
            />
          </div> */}

            <div className="form-group">
              <div className="flex-row justify-between items-center mb-1">
                <label className="mb-0">Summary</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{
                    fontSize: '13px',
                    fontWeight: '400',
                    color: form.summary.length > 400 ? '#DC2626' : '#6B7280',
                    transition: 'color 0.2s'
                  }}>
                    {form.summary.length} / 400
                  </span>
                  {form.summary.length > 400 && (
                    <span style={{
                      fontSize: '12px',
                      background: '#FEE2E2',
                      color: '#DC2626',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontWeight: '400',
                      animation: 'pulse 1s infinite'
                    }}>
                      +{form.summary.length - 400} extra
                    </span>
                  )}
                </div>
              </div>
              <textarea
                value={form.summary}
                onChange={(e) => set('summary', e.target.value)}
                placeholder="Type in English..."
                rows={7}
                style={{
                  borderColor: form.summary.length > 400 ? '#DC2626' : undefined,
                  backgroundColor: form.summary.length > 400 ? '#FEF2F2' : undefined,
                  resize: 'vertical'
                }}
                required
              />
              {form.summary.length > 400 && (
                <span style={{ fontSize: '12px', color: '#DC2626', marginTop: '6px', display: 'block' }}>
                  ⚠️ Summary exceeds limit by {form.summary.length - 400} characters. Please trim it down to 400 characters max.
                </span>
              )}
              {errors.summary && <span className="field-error">{errors.summary}</span>}
            </div>


            {(form.title_en || form.summary_en) && (
              <div className="form-group" style={{ padding: '16px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <h4 style={{ margin: 0, color: '#495057' }}>English Translation</h4>
                  <button
                    type="button"
                    className="btn btn-xs btn-ghost"
                    onClick={() => handleCopySingleTranslation('English', form.title_en, form.summary_en)}
                    style={{ border: '1px solid #ccc', display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 8px', fontSize: '12px' }}
                  >
                    📋 Copy to Clipboard
                  </button>
                </div>
                <div className="form-group mb-sm">
                  <label className="mb-0">Title (English)</label>
                  <input
                    type="text"
                    value={form.title_en}
                    onChange={(e) => set('title_en', e.target.value)}
                    placeholder="English title..."
                  />
                </div>
                <div className="form-group mb-0">
                  <label className="mb-0">Summary (English)</label>
                  <textarea
                    value={form.summary_en}
                    onChange={(e) => set('summary_en', e.target.value)}
                    placeholder="English summary..."
                    rows={4}
                  />
                </div>
              </div>
            )}

            {(form.title_hi || form.summary_hi) && (
              <div className="form-group" style={{ padding: '16px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <h4 style={{ margin: 0, color: '#495057' }}>Hindi Translation</h4>
                  <button
                    type="button"
                    className="btn btn-xs btn-ghost"
                    onClick={() => handleCopySingleTranslation('Hindi', form.title_hi, form.summary_hi)}
                    style={{ border: '1px solid #ccc', display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 8px', fontSize: '12px' }}
                  >
                    📋 Copy to Clipboard
                  </button>
                </div>
                <div className="form-group mb-sm">
                  <label className="mb-0">Title (Hindi)</label>
                  <input
                    type="text"
                    value={form.title_hi}
                    onChange={(e) => set('title_hi', e.target.value)}
                    placeholder="Hindi title..."
                  />
                </div>
                <div className="form-group mb-0">
                  <label className="mb-0">Summary (Hindi)</label>
                  <textarea
                    value={form.summary_hi}
                    onChange={(e) => set('summary_hi', e.target.value)}
                    placeholder="Hindi summary..."
                    rows={4}
                  />
                </div>
              </div>
            )}


            {/* AUTHOR NAME (Restored) */}
            {/* <div className="form-group">
            <div className="flex-row justify-between items-center mb-1">
              <label className="mb-0">Author Name</label>
              <button type="button" className="btn btn-xs btn-ghost ai-btn" onClick={() => handleAiGenerate('author')} disabled={loading}>
                ✨ AI Random Name
              </button>
            </div>
            <input
              type="text"
              value={form.author_name}
              onChange={(e) => set('author_name', e.target.value)}
              placeholder="e.g. Rajesh Kumar..."
            />
          </div> */}

            <div className="form-group" style={{ display: 'none' }}>
              <label>Publish Date & Time</label>
              <input
                type="datetime-local"
                value={form.published_at}
                onChange={(e) => set('published_at', e.target.value)}
              />
            </div>

            <div className="form-group">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <label style={{ margin: 0 }}>Media Type</label>
                <span style={{ fontSize: '12px', color: '#6B7280' }}>720 × 590 px</span>
              </div>
              <div className="radio-group">
                <label className={`radio-card ${form.media_type === 'image' ? 'selected' : ''}`}>
                  <input type="radio" value="image" checked={form.media_type === 'image'}
                    onChange={() => set('media_type', 'image')} />
                  Image
                </label>
                <label className={`radio-card ${form.media_type === 'video' ? 'selected' : ''}`}>
                  <input type="radio" value="video" checked={form.media_type === 'video'}
                    onChange={() => set('media_type', 'video')} />
                  Video
                </label>
              </div>
            </div>

            {form.media_type === 'image' && (
              <>
                <div className="form-group">
                  <label style={{ fontWeight: 700, color: errors.image_url ? '#EF233C' : undefined }}>
                    Article Image (Web & App) <span style={{ color: '#EF233C' }}>*</span>
                  </label>
                  <div className={`upload-area ${errors.image_url ? 'upload-area-error' : ''}`} tabIndex={0} onClick={() => handleFileSelect('image')} onPaste={handlePasteImage}>
                    {form.image_url ? (
                      <img src={form.image_url} alt="preview" className="upload-preview" />
                    ) : (
                      <p style={{ color: errors.image_url ? '#EF233C' : undefined, fontWeight: errors.image_url ? 600 : undefined }}>
                        {uploading ? 'Processing...' : 'Click to select & crop — or paste an image (Ctrl+V)'}
                      </p>
                    )}
                  </div>
                  {errors.image_url && (
                    <span className="field-error" style={{ color: '#EF233C', fontWeight: 600, fontSize: '13px', marginTop: '6px', display: 'block' }}>
                      {errors.image_url}
                    </span>
                  )}
                  <input type="text" value={form.image_url} onChange={(e) => set('image_url', e.target.value)} placeholder="Or paste image URL" className="mt-sm" style={{ display: 'none' }} />
                </div>

                {/* Gallery Images Section */}
                <div className="form-group" style={{ marginTop: '20px' }} tabIndex={0} onPaste={handleGalleryPasteImage}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <label style={{ margin: 0, fontWeight: 700 }}>Gallery Images</label>
                    <span style={{ fontSize: '12px', color: '#6B7280' }}>Click, drag & drop, or paste (Ctrl+V)</span>
                  </div>
                  <div 
                    className="upload-area"
                    tabIndex={0}
                    onPaste={handleGalleryPasteImage}
                    onClick={() => {
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.multiple = true;
                      input.accept = 'image/jpeg,image/jpg,image/png,image/webp';
                      input.onchange = (e) => handleGalleryFilesUpload(Array.from(e.target.files));
                      input.click();
                    }}
                    style={{ cursor: 'pointer', padding: '24px 16px', border: '2px dashed #CBD5E1', borderRadius: '10px', textAlign: 'center', backgroundColor: '#F8FAFC' }}
                  >
                    <p style={{ margin: 0, fontSize: '14px', color: '#475569', fontWeight: 500 }}>
                      {galleryUploading ? 'Uploading gallery images...' : 'Click to select multiple images, drag & drop, or paste (Ctrl+V)'}
                    </p>
                  </div>

                  {/* Gallery Thumbnails Grid & Plus Card */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: '12px', marginTop: '16px' }}>
                    {(form.images || []).map((imgUrl, index) => (
                      <div key={index} style={{ position: 'relative', borderRadius: '8px', border: '1px solid #E2E8F0', overflow: 'hidden', backgroundColor: '#FFF', height: '108px', display: 'flex', flexDirection: 'column' }}>
                        <img src={typeof imgUrl === 'string' ? imgUrl : imgUrl.image_url || imgUrl.url} alt={`gallery-${index}`} style={{ width: '100%', height: '80px', objectFit: 'cover', display: 'block' }} />
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 6px', background: '#F1F5F9', borderTop: '1px solid #E2E8F0', height: '28px', boxSizing: 'border-box' }}>
                          <div style={{ display: 'flex', gap: '2px' }}>
                            <button 
                              type="button" 
                              disabled={index === 0} 
                              onClick={(e) => { e.stopPropagation(); moveGalleryImage(index, -1); }} 
                              style={{ border: 'none', background: 'transparent', cursor: index === 0 ? 'default' : 'pointer', opacity: index === 0 ? 0.3 : 1, fontSize: '12px', padding: '2px 4px' }}
                              title="Move Left"
                            >
                              ◀
                            </button>
                            <button 
                              type="button" 
                              disabled={index === (form.images || []).length - 1} 
                              onClick={(e) => { e.stopPropagation(); moveGalleryImage(index, 1); }} 
                              style={{ border: 'none', background: 'transparent', cursor: index === (form.images || []).length - 1 ? 'default' : 'pointer', opacity: index === (form.images || []).length - 1 ? 0.3 : 1, fontSize: '12px', padding: '2px 4px' }}
                              title="Move Right"
                            >
                              ▶
                            </button>
                          </div>
                          <button 
                            type="button" 
                            onClick={(e) => { e.stopPropagation(); removeGalleryImage(index); }} 
                            style={{ border: 'none', background: 'transparent', color: '#EF233C', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold', padding: '2px 4px' }}
                            title="Remove Image"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    ))}

                    {/* Add Images (+) Card */}
                    <div 
                      onClick={() => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.multiple = true;
                        input.accept = 'image/jpeg,image/jpg,image/png,image/webp';
                        input.onchange = (e) => handleGalleryFilesUpload(Array.from(e.target.files));
                        input.click();
                      }}
                      style={{
                        height: '108px',
                        border: '2px dashed #CBD5E1',
                        borderRadius: '8px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        backgroundColor: '#F8FAFC',
                        transition: 'all 0.2s ease',
                        color: '#475569',
                        boxSizing: 'border-box'
                      }}
                      title="Add Images"
                    >
                      <span style={{ fontSize: '24px', fontWeight: 'bold', lineHeight: 1 }}>+</span>
                      <span style={{ fontSize: '12px', fontWeight: 600, marginTop: '4px' }}>Add Images</span>
                    </div>
                  </div>
                </div>
              </>
            )}

            {form.media_type === 'video' && (
              <>
                <div className="form-group">
                  <label>Video URL</label>
                  <input type="text" value={form.video_url} onChange={(e) => set('video_url', e.target.value)} placeholder="MP4 URL..." />
                  <button type="button" className="btn btn-xs mt-xs" onClick={() => handleFileSelect('video')}>Upload Video</button>
                </div>
                <div className="form-group">
                  <label>Video Thumbnail (App & Web)</label>
                  <DragDropImageUpload
                    type="thumbnail"
                    uploading={uploading}
                    imageUrl={form.thumbnail_url}
                    onFileSelect={handleFileSelect}
                  />
                  <input type="text" value={form.thumbnail_url} onChange={(e) => set('thumbnail_url', e.target.value)}
                    placeholder="Or paste thumbnail URL" className="mt-sm" style={{ display: 'none' }} />
                </div>
              </>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)' }}>
            <div className="form-group">
              <label>Category</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                {SHOW_FULL_CATEGORY_PICKER ? (
                  <>
                    {['Viral News', 'International', 'National', 'Telangana', 'Andhra Pradesh'].map(cat => {
                      let isSelected = false;
                      if (form.category) {
                        const cTrim = form.category.trim();
                        if (cTrim === cat || (cat === 'Viral News' && cTrim === 'వైరల్ వార్తలు') || (cat === 'International' && cTrim === 'అంతర్జాతీయ') || (cat === 'National' && cTrim === 'జాతీయ') || (cat === 'Telangana' && cTrim === 'తెలంగాణ') || (cat === 'Andhra Pradesh' && cTrim === 'ఆంధ్రప్రదేశ్')) {
                          isSelected = true;
                        }
                      }

                      return (
                        <label key={cat} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px', color: isSelected ? '#EF233C' : '#333', fontWeight: '400' }}>
                          <input
                            type="radio"
                            name="quick_category"
                            checked={isSelected}
                            onChange={() => {
                              let dbVal = cat;
                              const found = categories.find(c => c.name.trim().toLowerCase() === cat.toLowerCase());
                              if (found) dbVal = found.name;

                              set('category', dbVal);
                              if (cat === 'Telangana' || cat === 'Andhra Pradesh') {
                                setSelectedState(cat);
                              } else if (cat === 'Viral News') {
                                if (selectedState && selectedState.trim() !== 'Telangana' && selectedState.trim() !== 'Andhra Pradesh') {
                                  setSelectedState('Telangana');
                                }
                              } else {
                                setSelectedState('All India');
                              }
                            }}
                            style={{ cursor: 'pointer', width: '16px', height: '16px', accentColor: '#EF233C' }}
                          />
                          {cat}
                        </label>
                      );
                    })}

                    <OthersDropdown
                      options={categories.filter(c => !['Viral News', 'International', 'National', 'Telangana', 'Andhra Pradesh', 'వైరల్ వార్తలు', 'అంతర్జాతీయ', 'జాతీయ', 'తెలంగాణ', 'ఆంధ్రప్రదేశ్'].includes(c.name.trim()))}
                      value={form.category}
                      onChange={(val) => {
                        set('category', val);
                        const vTrim = val.trim();
                        if (vTrim === 'Telangana' || vTrim === 'Andhra Pradesh' || vTrim === 'తెలంగాణ' || vTrim === 'ఆంధ్రప్రదేశ్') {
                          setSelectedState(vTrim === 'తెలంగాణ' ? 'Telangana' : vTrim === 'ఆంధ్రప్రదేశ్' ? 'Andhra Pradesh' : vTrim);
                        } else if (vTrim === 'Viral News' || vTrim === 'వైరల్ వార్తలు') {
                          if (selectedState && selectedState.trim() !== 'Telangana' && selectedState.trim() !== 'Andhra Pradesh') {
                            setSelectedState('Telangana');
                          }
                        } else {
                          setSelectedState('All India');
                        }
                      }}
                      quickCategoriesList={['Viral News', 'International', 'National', 'Telangana', 'Andhra Pradesh', 'వైరల్ వార్తలు', 'అంతర్జాతీయ', 'జాతీయ', 'తెలంగాణ', 'ఆంధ్రప్రదేశ్']}
                    />
                  </>
                ) : (
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px', color: '#EF233C', fontWeight: '400' }}>
                    <input
                      type="radio"
                      name="quick_category"
                      checked
                      readOnly
                      style={{ cursor: 'pointer', width: '16px', height: '16px', accentColor: '#EF233C' }}
                    />
                    News
                  </label>
                )}
              </div>
            </div>

            {SHOW_STATE_CITY_PICKER && (
              <div className="form-row">
                <div className="form-group">
                  <label>State</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', minHeight: '42px' }}>
                    {['Andhra Pradesh', 'Telangana', 'All India'].map(stateName => {
                      let isSelected = false;
                      if (selectedState) {
                        const sTrim = selectedState.trim();
                        if (sTrim === stateName || (stateName === 'Andhra Pradesh' && (sTrim === 'ఆంధ్రప్రదేశ్' || sTrim === 'ఆంధ్ర ప్రదేశ్')) || (stateName === 'Telangana' && sTrim === 'తెలంగాణ') || (stateName === 'All India' && (sTrim === 'అల్ ఇండియా' || sTrim === 'అల్ ఇండియా '))) {
                          isSelected = true;
                        }
                      }
                      return (
                        <label key={stateName} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px', color: isSelected ? '#EF233C' : '#333', fontWeight: '400' }}>
                          <input
                            type="radio"
                            name="quick_state"
                            checked={isSelected}
                            onChange={() => setSelectedState(stateName)}
                            style={{ cursor: 'pointer', width: '16px', height: '16px', accentColor: '#EF233C' }}
                          />
                          {stateName}
                        </label>
                      );
                    })}
                  </div>
                </div>
                <SearchableSelect
                  label="City"
                  value={form.city}
                  options={cities}
                  onChange={(val) => set('city', val)}
                />
              </div>
            )}

            <div className="form-group flex-row gap-8" style={{ alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <label className="checkbox-label">
                  <input type="checkbox" checked={form.is_breaking}
                    onChange={(e) => set('is_breaking', e.target.checked)} />
                  <span className="breaking-tag">Breaking News</span>
                </label>
              </div>
              <div style={{ flex: 1 }}>
                <label className="checkbox-label">
                  <input type="checkbox" checked={form.is_featured}
                    onChange={(e) => set('is_featured', e.target.checked)} />
                  <span className="featured-tag">Featured Article</span>
                </label>
              </div>
            </div>

            {form.is_breaking && (
              <div className="form-group">
                <label>Custom Notification Title (Optional)</label>
                <input
                  type="text"
                  value={form.custom_notification_title || ''}
                  onChange={(e) => set('custom_notification_title', e.target.value)}
                  placeholder="Example: Heavy Rain Alert in Hyderabad"
                />
                <span style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px', display: 'block' }}>
                  Leave blank to use the original article title.
                </span>
              </div>
            )}
          </div>
        </form>

        <div className="publish-sidebar"></div>
      </div>


      {showWebPreview && (
        <WebPreviewModal
          article={form}
          onClose={() => setShowWebPreview(false)}
        />
      )}

      {cropper.active && (
        <ImageCropper
          image={cropper.image}
          onCropComplete={handleCropComplete}
          onCancel={() => setCropper({ active: false, image: null, type: null })}
        />
      )}

      {/* Duplicate Warning Modal */}
      {duplicateWarning && (
        <div className="modal-overlay" onClick={() => setDuplicateWarning(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 540, borderRadius: 16, overflow: 'hidden' }}>
            <div style={{ background: '#FEF2F2', padding: '20px 24px', borderBottom: '1px solid #FEE2E2', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#FEE2E2', color: '#DC2626', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>
                ⚠️
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#991B1B' }}>Duplicate Article Found</h3>
                <p style={{ margin: 0, fontSize: 13, color: '#B91C1C', marginTop: 2 }}>
                  This looks very similar to an already-published article.
                </p>
              </div>
              <button className="modal-close" onClick={() => setDuplicateWarning(null)}>
                <MdClose />
              </button>
            </div>

            <div className="modal-body" style={{ padding: 24, maxHeight: '55vh', overflowY: 'auto' }}>
              <p style={{ fontSize: 13, color: '#64748B', margin: '0 0 16px 0' }}>
                {duplicateWarning.duplicates.length} similar article{duplicateWarning.duplicates.length > 1 ? 's' : ''} found — review before publishing.
              </p>

              {duplicateWarning.duplicates.map((dup) => (
                <div key={dup.id} style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 12, padding: 16, marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Similarity Score
                    </span>
                    <span style={{ fontSize: 15, fontWeight: 800, color: '#E11D48', padding: '2px 10px', background: '#FFE4E6', borderRadius: 20 }}>
                      {dup.score}%
                    </span>
                  </div>

                  <div style={{ fontSize: 15, fontWeight: 600, color: '#0F172A', marginBottom: 6, lineHeight: 1.4 }}>
                    {dup.title}
                  </div>

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, fontSize: 12, color: '#475569', marginBottom: 10 }}>
                    <div><strong>Reporter:</strong> {dup.reporter_name || 'Admin'}</div>
                    <div><strong>Category:</strong> {dup.category}</div>
                    <div><strong>Published Date:</strong> {new Date(dup.published_at).toLocaleDateString()}</div>
                  </div>

                  <button
                    className="btn btn-ghost btn-sm"
                    style={{ borderColor: '#0284C7', color: '#0284C7', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                    onClick={() => setViewMatchedArticle(dup)}
                  >
                    <MdVisibility size={14} /> View Article
                  </button>
                </div>
              ))}
            </div>

            <div className="modal-footer" style={{ padding: '16px 24px', background: '#F8FAFC', borderTop: '1px solid #E2E8F0', display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button
                className="btn btn-ghost"
                onClick={() => setDuplicateWarning(null)}
              >
                Edit Article
              </button>

              <button
                className="btn btn-primary"
                onClick={() => {
                  setDuplicateWarning(null);
                  doPublish();
                }}
                disabled={loading}
              >
                {loading ? 'Publishing...' : 'Publish Anyway'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Matched Duplicate Article Preview Modal */}
      {viewMatchedArticle && (
        <div className="modal-overlay" onClick={() => setViewMatchedArticle(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 640, borderRadius: 16, overflow: 'hidden' }}>
            <div className="modal-header" style={{ background: '#F8FAFC', padding: '16px 20px', borderBottom: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#0F172A' }}>
                Matched Duplicate Article Details (ID: #{viewMatchedArticle.id})
              </h3>
              <button className="modal-close" onClick={() => setViewMatchedArticle(null)}><MdClose /></button>
            </div>

            <div className="modal-body" style={{ padding: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#FFF1F2', border: '1px solid #FECDD3', padding: '10px 14px', borderRadius: 10, marginBottom: 16 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#881337' }}>Similarity Score</span>
                <span style={{ fontSize: 18, fontWeight: 800, color: '#E11D48' }}>{viewMatchedArticle.score}% Match</span>
              </div>

              {(viewMatchedArticle.image_url || viewMatchedArticle.thumbnail_url) && (
                <img
                  src={viewMatchedArticle.image_url || viewMatchedArticle.thumbnail_url}
                  alt=""
                  style={{ width: '100%', maxHeight: 240, objectFit: 'cover', borderRadius: 10, marginBottom: 16 }}
                />
              )}

              <h4 style={{ margin: '0 0 10px 0', fontSize: 16, fontWeight: 700, color: '#0F172A', lineHeight: 1.4 }}>
                {viewMatchedArticle.title}
              </h4>

              {viewMatchedArticle.summary && (
                <p style={{ fontSize: 14, color: '#334155', lineHeight: 1.5, marginBottom: 16, background: '#F8FAFC', padding: 12, borderRadius: 8, border: '1px solid #E2E8F0' }}>
                  {viewMatchedArticle.summary}
                </p>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 12.5, color: '#475569', background: '#FAFAFC', padding: 12, borderRadius: 10 }}>
                <div><strong>Article ID:</strong> #{viewMatchedArticle.id}</div>
                <div><strong>Reporter:</strong> {viewMatchedArticle.reporter_name || 'Admin'}</div>
                <div><strong>Category:</strong> {viewMatchedArticle.category || 'General'}</div>
                <div><strong>Published Date:</strong> {new Date(viewMatchedArticle.published_at).toLocaleDateString()}</div>
              </div>
            </div>

            <div className="modal-footer" style={{ padding: '14px 20px', background: '#F8FAFC', borderTop: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {viewMatchedArticle.slug ? (
                <a
                  href={`http://localhost:3000/${viewMatchedArticle.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-ghost"
                  style={{ borderColor: '#0284C7', color: '#0284C7', fontSize: 13 }}
                >
                  Open Web Article ↗
                </a>
              ) : <div />}
              <button className="btn btn-primary" onClick={() => setViewMatchedArticle(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
